import React, { useState } from 'react';
import { Upload, X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useGalleryPhotos, useDeletePhoto } from '../../hooks/usePhotos';
import { Avatar } from '../auth/Profile';
import UploadPhotoModal from './UploadPhotoModal';
import './Gallery.css';

const VISIBILITY_LABELS = {
  public: 'Public',
  family: 'Family',
  group: 'Group',
  reservation: 'Reservation',
  owners: 'Owners',
};

const FILTERS = [
  { value: null, label: 'All' },
  { value: 'public', label: 'Public' },
  { value: 'family', label: 'Family' },
  { value: 'group', label: 'Group' },
  { value: 'reservation', label: 'Reservation' },
  { value: 'owners', label: 'Owners' },
];

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function Lightbox({ photos, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const photo = photos[index];

  function prev(e) { e.stopPropagation(); setIndex(i => (i - 1 + photos.length) % photos.length); }
  function next(e) { e.stopPropagation(); setIndex(i => (i + 1) % photos.length); }

  function handleKeyDown(e) {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + photos.length) % photos.length);
    if (e.key === 'ArrowRight') setIndex(i => (i + 1) % photos.length);
  }

  return (
    <div className="lightbox-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={0} autoFocus>
      <button className="lightbox-close" onClick={onClose}><X size={24} /></button>
      {photos.length > 1 && (
        <>
          <button className="lightbox-nav lightbox-prev" onClick={prev}><ChevronLeft size={32} /></button>
          <button className="lightbox-nav lightbox-next" onClick={next}><ChevronRight size={32} /></button>
        </>
      )}
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        <img src={photo.url} alt={photo.caption || photo.filename} className="lightbox-img" />
        {(photo.caption || photo.uploader_name) && (
          <div className="lightbox-meta">
            {photo.caption && <p className="lightbox-caption">{photo.caption}</p>}
            <p className="lightbox-uploader">
              <Avatar name={photo.uploader_name} size="small" avatarUrl={photo.uploader_avatar} />
              {photo.uploader_name} · {timeAgo(photo.created_at)}
              <span className={`visibility-badge badge-${photo.visibility}`}>
                {VISIBILITY_LABELS[photo.visibility]}
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Gallery() {
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [showUpload, setShowUpload] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const { data, isLoading, error } = useGalleryPhotos({ visibility: activeFilter, page });
  const deletePhoto = useDeletePhoto();

  const photos = data?.photos || [];
  const total = data?.total || 0;
  const limit = data?.limit || 20;
  const totalPages = Math.ceil(total / limit);

  function handleFilterChange(value) {
    setActiveFilter(value);
    setPage(1);
  }

  async function handleDelete(e, photoId) {
    e.stopPropagation();
    if (!window.confirm('Delete this photo?')) return;
    await deletePhoto.mutateAsync(photoId);
    if (lightboxIndex !== null) setLightboxIndex(null);
  }

  const isAdminOrManager = user.role === 'admin' || user.role === 'manager';

  return (
    <div className="gallery-page">
      <div className="gallery-header">
        <h2>Photo Gallery</h2>
        <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
          <Upload size={16} /> Share Photo
        </button>
      </div>

      {/* Filter tabs */}
      <div className="gallery-filters">
        {FILTERS.map(f => (
          <button
            key={String(f.value)}
            className={`filter-tab ${activeFilter === f.value ? 'active' : ''}`}
            onClick={() => handleFilterChange(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="gallery-status">Loading photos…</p>}
      {error && <p className="gallery-status error">Failed to load photos.</p>}

      {!isLoading && !error && photos.length === 0 && (
        <div className="gallery-empty">
          <p>No photos here yet.</p>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            <Upload size={16} /> Be the first to share
          </button>
        </div>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((photo, idx) => (
            <div
              key={photo.id}
              className="photo-card"
              onClick={() => setLightboxIndex(idx)}
            >
              <div className="photo-card-thumb">
                <img src={photo.url} alt={photo.caption || photo.filename} loading="lazy" />
                <span className={`visibility-badge badge-${photo.visibility}`}>
                  {VISIBILITY_LABELS[photo.visibility]}
                </span>
              </div>
              <div className="photo-card-meta">
                <div className="photo-card-uploader">
                  <Avatar name={photo.uploader_name} size="small" avatarUrl={photo.uploader_avatar} />
                  <span>{photo.uploader_name}</span>
                  <span className="photo-card-time">{timeAgo(photo.created_at)}</span>
                </div>
                {photo.caption && <p className="photo-card-caption">{photo.caption}</p>}
              </div>
              {(photo.user_id === user.id || isAdminOrManager) && (
                <button
                  className="photo-delete-btn"
                  onClick={e => handleDelete(e, photo.id)}
                  title="Delete photo"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="gallery-pagination">
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <span>{page} / {totalPages}</span>
          <button
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {showUpload && (
        <UploadPhotoModal onClose={() => setShowUpload(false)} />
      )}

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
